import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import CONFIG from '../config/config';

export const fetchSubscriptions = async (accountGuid) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/subs/list`;
        const response = await axios.get(url, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'accountGuid': accountGuid,
            },
        });

        if (response.status === 200) {
            return response.data; // Subscription records
        } else {
            console.error(`Error fetching subscriptions: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error fetching subscriptions:', error);
        throw error;
    }
};

export const getSubscriptionByGuid = async (subscriptionGuid) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/subs/get-by-guid`;
        const response = await axios.get(url, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'subscriptionGuid': subscriptionGuid,
            },
        });

        if (response.status === 200) {
            return response.data; // Subscription details
        } else {
            console.error(`Error fetching subscription by GUID: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error fetching subscription by GUID:', error);
        throw error;
    }
};

/**
 * Create a new subscription.
 * @param {Object} subscriptionData
 * @returns {Promise<Object>} - The created subscription GUID
 */
export const createSubscription = async (subscriptionData) => {
    const subscriptionGuid = uuidv4();
    const payload = {
        ...subscriptionData,
        subscriptionGuid,
    };

    try {
        const url = `${CONFIG.API.BASE_URL}/subs/create`;
        const response = await axios.post(url, payload, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 201 || response.status === 200) {
            return response.data; // Successfully created subscription data
        } else {
            console.error(`Error creating subscription: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error creating subscription:', error);
        throw error;
    }
};

export const updateSubscriptionFields = async (subscriptionGuid, updatedFields, action = null) => {
    try {
        let body;

        if (action) {
            if (updatedFields.assignedUsers) {
                body = {
                    updatedFields: {
                        assignedUsers: { action, users: updatedFields.assignedUsers },
                    },
                };
            } else if (updatedFields.assignedCustomers) {
                body = {
                    updatedFields: {
                        assignedCustomers: { action, customers: updatedFields.assignedCustomers },
                    },
                };
            } else {
                throw new Error('Action specified but no valid data for assignedUsers or assignedCustomers.');
            }
        } else {
            body = { updatedFields };
        }

        const url = `${CONFIG.API.BASE_URL}/subs/updateFields`;
        const response = await axios.patch(url, body, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'subscriptionGuid': subscriptionGuid,
            },
        });

        if (response.status === 200) {
            return response.data; // Updated subscription data
        } else {
            console.error(`Error updating subscription fields: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error updating subscription fields:', error);
        throw error;
    }
};

export const updateSubscriptionFieldsOLD = async (subscriptionGuid, updatedFields, action = null) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/subs/updateFields`;
        const body = action
            ? { updatedFields: { assignedUsers: { action, users: updatedFields.assignedUsers } } }
            : { updatedFields };

        const response = await axios.patch(url, body, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'subscriptionGuid': subscriptionGuid,
            },
        });

        if (response.status === 200) {
            return response.data; // Updated subscription data
        } else {
            console.error(`Error updating subscription fields: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error updating subscription fields:', error);
        throw error;
    }
};

export const removeUserFromSubscription = async (subscriptionGuid, userGuid) => {
    // Validate input parameters
    if (!subscriptionGuid || typeof subscriptionGuid !== 'string' || subscriptionGuid.trim() === '') {
        throw new Error('Invalid or missing subscriptionGuid.');
    }
    if (!userGuid || typeof userGuid !== 'string' || userGuid.trim() === '') {
        throw new Error('Invalid or missing userGuid.');
    }

    try {
        const url = `${CONFIG.API.BASE_URL}/subs/updateFields`;
        const response = await axios.patch(url,
            {
                updatedFields: {
                    assignedUsers: {
                        action: 'remove',
                        users: [userGuid],
                    },
                },
            },
            {
                headers: {
                    ...CONFIG.DEFAULT_HEADERS,
                    'accessToken': CONFIG.API.ACCESS_TOKEN,
                    'subscriptionGuid': subscriptionGuid,
                },
            });

        if (response.status === 200) {
            return response.data;
        } else {
            console.error(`Error removing user from subscription: ${response.status}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error removing user from subscription:', error);
        throw error;
    }
};

export const removeCustomerFromSubscription = async (subscriptionGuid, customerGuid) => {
    // Validate input parameters
    if (!subscriptionGuid || typeof subscriptionGuid !== 'string' || subscriptionGuid.trim() === '') {
        throw new Error('Invalid or missing subscriptionGuid.');
    }
    if (!customerGuid || typeof customerGuid !== 'string' || customerGuid.trim() === '') {
        throw new Error('Invalid or missing customerGuid.');
    }

    try {
        const url = `${CONFIG.API.BASE_URL}/subs/updateFields`;
        const response = await axios.patch(
            url,
            {
                updatedFields: {
                    assignedCustomers: {
                        action: 'remove',
                        customers: [customerGuid],
                    },
                },
            },
            {
                headers: {
                    ...CONFIG.DEFAULT_HEADERS,
                    'accessToken': CONFIG.API.ACCESS_TOKEN,
                    'subscriptionGuid': subscriptionGuid,
                },
            }
        );

        if (response.status === 200) {
            return response.data; // Successfully removed customer
        } else {
            console.error(`Error removing customer from subscription: ${response.status}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error removing customer from subscription:', error);
        throw error;
    }
};